import { useEffect, useMemo, useState } from 'react'

import { keycloak } from 'lib/keycloak'
import datadog from 'utils/datadog'

import { KeycloakContext } from './keycloak.context'
import type { IKeycloakContext, IKeycloakProviderProps } from './keycloak.types'
import { ensureKeycloakInitialized } from './keycloak.utils'

// "Dumb" provider: it owns no init logic of its own — ensureKeycloakInitialized()
// (an external, memoized singleton) does the bootstrap, and this component only
// reflects its readiness into context. The provider always renders children and
// exposes { keycloak, initialized }; the loading/error UI is AuthGate's job,
// which reads `initialized` to decide when to show its <Loading> screen.
export function KeycloakProvider({ children }: IKeycloakProviderProps) {
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    let active = true

    ensureKeycloakInitialized()
      .catch((error: unknown) => {
        // init() rejects only on real infra/config errors — check-sso resolves `false`
        // when the user is simply unauthenticated. Log and swallow so it doesn't surface
        // as an unhandled rejection; `initialized` still flips below so AuthGate renders.
        datadog.dispatchGenericErrorLog({
          log: { from: 'KeycloakProvider', name: 'keycloak_init_failed', message: 'keycloak.init() rejected' },
          error: error instanceof Error ? error : new Error(typeof error === 'string' ? error : 'non-Error rejection'),
        })
      })
      .finally(() => {
        if (active) {
          setInitialized(true)
        }
      })

    return () => {
      active = false
    }
  }, [])

  const value = useMemo<IKeycloakContext>(() => ({ keycloak, initialized }), [initialized])

  return <KeycloakContext.Provider value={value}>{children}</KeycloakContext.Provider>
}
